import { useLayoutEffect } from 'react';
import { isForeground, subscribeForeground } from './AppStateService';
import { createPositionRuntime, createPositionPermissionScope, POSITION_PERMISSION_GRANTED_EVENT } from './PositionSnapshotService';
import { sharedGeolocation } from './GeolocationWatchService';

// Mount only at the authenticated root. GPS permission is never requested by an
// ordinary mutation or a profile read: explicit opt-in/device checks and user
// Submit own the prompt. Permission change arms this watch after a grant.
export function usePositionCapture(owner, backend) {
  useLayoutEffect(() => {
    if (!owner) return undefined;
    const permissionScope = createPositionPermissionScope(owner);
    let alive = true, version = 0, permission = null, explicitGrant = false, consentEnabled = false, deviceDenied = false;
    let retryTimer = null;
    const stopRetry = () => { if (retryTimer !== null) window.clearTimeout(retryTimer); retryTimer = null; };
    const runtime = createPositionRuntime({
      geolocation: sharedGeolocation, foreground: isForeground, subscribe: subscribeForeground,
      onPermissionDenied: () => { explicitGrant = false; deviceDenied = true; version++; },
    });
    backend.positionRuntime = runtime;
    backend.positionOwner = owner;
    const update = async () => {
      const request = ++version;
      stopRetry();
      runtime.configure(owner, false);
      const epoch = backend.getLocationConsentEpoch();
      if (epoch === null || !isForeground()) return;
      try {
        const user = await backend.fetchUserProfile({ requireAuthoritative: true });
        if (!alive || request !== version || epoch !== backend.getLocationConsentEpoch() || String(user?._id || user?.id) !== String(owner)) return;
        if (user?.location_capture_enabled !== true) {
          if (consentEnabled) explicitGrant = false;
          consentEnabled = false;
          return;
        }
        consentEnabled = true;
        if (!navigator.permissions?.query) { runtime.configure(owner, explicitGrant && permission?.state !== 'denied'); return; }
        let status;
        try { status = await navigator.permissions.query({ name: 'geolocation' }); }
        catch (_) {
          if (alive && request === version && epoch === backend.getLocationConsentEpoch() && isForeground()) {
            runtime.configure(owner, explicitGrant && permission?.state !== 'denied');
          }
          return;
        }
        if (!alive || request !== version || epoch !== backend.getLocationConsentEpoch() || !isForeground()) return;
        permission?.removeEventListener?.('change', permissionChanged);
        permission = status; permission.addEventListener?.('change', permissionChanged);
        if (status.state === 'denied') explicitGrant = false;
        runtime.configure(owner, !deviceDenied && (status.state === 'granted' || (status.state === 'prompt' && explicitGrant)));
      } catch (_) {
        // Only the current foreground lifecycle retries unknown consent. A
        // settled opt-out/denial does not poll or re-prompt; its explicit actions
        // and consent/foreground listeners still own reactivation.
        if (alive && request === version && epoch === backend.getLocationConsentEpoch() && isForeground()) {
          retryTimer = window.setTimeout(() => { retryTimer = null; update(); }, 5000);
        }
      }
    };
    const permissionChanged = () => { explicitGrant = false; deviceDenied = permission?.state !== 'granted'; update(); };
    const grant = event => {
      if (!alive || event.detail?.scope !== permissionScope.scope) return;
      explicitGrant = isForeground() && permission?.state !== 'denied';
      if (explicitGrant) deviceDenied = false;
      update();
    };
    window.addEventListener(POSITION_PERMISSION_GRANTED_EVENT, grant);
    const consent = backend.onLocationConsentChanged(update);
    const foreground = subscribeForeground(next => { if (next) update(); else { version++; stopRetry(); runtime.configure(owner, false); } });
    update();
    return () => {
      alive = false; version++; stopRetry(); permissionScope.dispose(); consent(); foreground(); window.removeEventListener(POSITION_PERMISSION_GRANTED_EVENT, grant); permission?.removeEventListener?.('change', permissionChanged); runtime.dispose();
      if (backend.positionRuntime === runtime) { backend.positionRuntime = null; backend.positionOwner = null; }
    };
  }, [owner, backend]);
}
